import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Checkbox from '@mui/material/Checkbox';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ParsedAddOn } from '@t/addOn.types';
import { formatPrice } from '@utils/formatPrice';
import { useConfigurationStore } from '@store/configurationStore';
import { DiscountBadge } from '@shared/components/display/DiscountBadge/DiscountBadge';
import { SelectableCard } from '@shared/components/SelectableCard';
import { ThemePackageChannelLogo } from './ThemePackageChannelLogo';

const MAX_LOGOS_WITHOUT_MORE = 4;
const MAX_LOGOS_WITH_MORE = 3;

interface ThemePackageCardProps {
  addOn: ParsedAddOn;
  isSelected: boolean;
  onToggle: () => void;
}

export const ThemePackageCard = ({ addOn, isSelected, onToggle }: ThemePackageCardProps) => {
  const { name, price, channels = [], discountTag, discountMessage } = addOn;
  const hasMore = channels.length > MAX_LOGOS_WITHOUT_MORE;
  const maxVisible = hasMore ? MAX_LOGOS_WITH_MORE : MAX_LOGOS_WITHOUT_MORE;
  const visibleChannels = channels.slice(0, maxVisible);
  const remainingCount = channels.length - maxVisible;

  const openChannelSideModal = useConfigurationStore((s) => s.openChannelSideModal);

  const handleMoreClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    openChannelSideModal(channels);
  };

  return (
    <SelectableCard selectionMode="multi" selected={isSelected} onSelect={onToggle}>
      {(selectable) => (
        <Card variant="themePackageCard" selected={isSelected} elevation={0} {...selectable}>
          <Checkbox checked={isSelected} tabIndex={-1} disableRipple />

          <Stack className="ThemePackageCard-content">
            <Typography variant="title.card">{name}</Typography>

            <Stack gap="10px">
              <Typography component="span" lineHeight={1.5}>
                <Typography component="span" variant="body.md" className="ThemePackageCard-price">
                  {formatPrice(price)}
                </Typography>
                <Typography component="span" variant="price.suffixLight">
                  {' / month'}
                </Typography>
              </Typography>

              {discountTag && (
                <DiscountBadge>
                  <Typography variant="discount.tag" className="ThemePackageCard-discountTag">
                    {discountTag}
                  </Typography>
                </DiscountBadge>
              )}

              {discountMessage && (
                <Typography variant="discount.text" className="ThemePackageCard-discountMessage">
                  {discountMessage}. Discount applied at checkout
                </Typography>
              )}
            </Stack>

            {channels.length > 0 && (
              <Stack className="ThemePackageCard-logoRow">
                {visibleChannels.map((channel, index) => (
                  <ThemePackageChannelLogo key={index} channel={channel} />
                ))}
                {remainingCount > 0 && (
                  <Box component="span" className="ThemePackageCard-moreLink" onClick={handleMoreClick}>
                    {`+${remainingCount} More`}
                  </Box>
                )}
              </Stack>
            )}
          </Stack>
        </Card>
      )}
    </SelectableCard>
  );
};
